import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore'
import type { DocumentData, QueryDocumentSnapshot } from 'firebase/firestore'
import { db, firebaseErrorCode, logFirebaseError, requireSession } from '../lib/firebase'
import { timestampToIso } from './customersService'
import { stripUndefined } from '../utils/firestore'
import type { Invoice } from '../types/invoice'

export const invoicesRef = collection(db, 'invoices')

/** Tæller der sikrer unikke fakturanumre — numre genbruges aldrig. */
const counterDoc = doc(db, 'counters', 'invoices')

function toInvoice(snapshot: QueryDocumentSnapshot<DocumentData>): Invoice {
  const data = snapshot.data()
  return {
    id: snapshot.id,
    invoiceNumber: data.invoiceNumber ?? '',
    customerId: data.customerId ?? '',
    caseId: data.caseId ?? undefined,
    quoteId: data.quoteId ?? undefined,
    customerName: data.customerName ?? '',
    customerAddress: data.customerAddress ?? '',
    customerPostalCode: data.customerPostalCode ?? '',
    customerCity: data.customerCity ?? '',
    customerEmail: data.customerEmail ?? '',
    customerPhone: data.customerPhone ?? '',
    invoiceDate: data.invoiceDate ?? '',
    dueDate: data.dueDate ?? '',
    paymentTerms: data.paymentTerms ?? '',
    reference: data.reference ?? '',
    description: data.description ?? '',
    lines: Array.isArray(data.lines) ? data.lines : [],
    subtotal: Number(data.subtotal) || 0,
    vatAmount: Number(data.vatAmount) || 0,
    total: Number(data.total) || 0,
    customerNote: data.customerNote ?? '',
    internalNote: data.internalNote ?? '',
    status: data.status ?? 'Kladde',
    createdAt: timestampToIso(data.createdAt),
    updatedAt: timestampToIso(data.updatedAt),
    sentAt: timestampToIso(data.sentAt) || undefined,
    paidAt: timestampToIso(data.paidAt) || undefined,
    dineroInvoiceId: data.dineroInvoiceId ?? undefined,
  }
}

export function subscribeInvoices(
  onChange: (invoices: Invoice[]) => void,
  onError: (error: unknown) => void,
): () => void {
  return onSnapshot(
    query(invoicesRef, orderBy('createdAt', 'desc')),
    (snapshot) => onChange(snapshot.docs.map(toInvoice)),
    onError,
  )
}

/**
 * Reserverer næste fakturanummer i en transaktion, fx FAK-2026-0001.
 * Afviser Firestore Rules tælleren, udledes nummeret af de eksisterende
 * fakturaer, så oprettelsen aldrig blokeres.
 */
export async function reserveInvoiceNumber(existingNumbers: string[] = []): Promise<string> {
  requireSession('reserveInvoiceNumber')
  const year = new Date().getFullYear()

  try {
    const next = await runTransaction(db, async (transaction) => {
      const snapshot = await transaction.get(counterDoc)
      const data = snapshot.exists() ? snapshot.data() : undefined
      const sameYear = data?.year === year
      const value = sameYear ? Number(data?.lastNumber ?? 0) + 1 : 1
      transaction.set(counterDoc, { year, lastNumber: value, updatedAt: serverTimestamp() })
      return value
    })
    return `FAK-${year}-${String(next).padStart(4, '0')}`
  } catch (error) {
    if (firebaseErrorCode(error) !== 'permission-denied') throw error
    logFirebaseError('reserveInvoiceNumber (counters) — bruger reservenummerering', error)

    const highest = existingNumbers.reduce((max, number) => {
      const match = /^FAK-(\d{4})-(\d+)$/.exec(number)
      if (!match || Number(match[1]) !== year) return max
      return Math.max(max, Number(match[2]))
    }, 0)
    return `FAK-${year}-${String(highest + 1).padStart(4, '0')}`
  }
}

export async function createInvoiceDoc(
  data: Omit<Invoice, 'id' | 'createdAt' | 'updatedAt'>,
): Promise<string> {
  requireSession('createInvoiceDoc')
  const created = await addDoc(invoicesRef, {
    ...stripUndefined(data as unknown as Record<string, unknown>),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  })
  return created.id
}

export async function updateInvoiceDoc(id: string, patch: Partial<Invoice>): Promise<void> {
  requireSession('updateInvoiceDoc')
  await updateDoc(doc(db, 'invoices', id), {
    ...stripUndefined(patch as Record<string, unknown>),
    updatedAt: serverTimestamp(),
  })
}

export async function deleteInvoiceDoc(id: string): Promise<void> {
  requireSession('deleteInvoiceDoc')
  await deleteDoc(doc(db, 'invoices', id))
}
